import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Facet, Sorting, Value } from '../../search-spring/types/types'
import Filter from '../../search-spring-page/components/Filter'
import ReadyToShipFilter from '../../search-spring-page/components/ReadyToShipFilter'
import SubCollectionFilter from '../../search-spring-page/components/SubCollectionFilter'
import useWindowSize from '../../search-spring/customHooks/useWindowSize'

type Props = {
  filters: Facet[]
  sorting: Sorting
  triggerSearch: () => void
  activeFilters: number
}

type SubCollectionLinks = {
  label: string
  url: string
}

declare global {
  interface Window {
    collectionDescription: string;
    collectionDescriptionMobile: string;
    subCollectionItems: SubCollectionLinks[]
  }
}


const CollectionPageHeader = ({ filters, sorting, triggerSearch, activeFilters}: Props) => {
  const hasReadyToShipFilter = filters.some(filter => filter.values && filter.values.find(label => label.value == "Ready to Ship"))
  const hasGlobalCollectionSubfilters = CD_STATE?.collectionDisrupter.subCollectionItems
  const hasLocalCollectionSubFilters = CD_STATE?.collectionDisrupter.localSubCollectionItems

  const windowSize = useWindowSize()
  const isTablet = windowSize.width <= 1024
  const scrollableDivRef = useRef<HTMLDivElement>(null)
  const [displayMore, setDisplayMore] = useState(false)
  const [showEllipsis, setShowEllipsis] = useState(false)
  let textDescription

  if (windowSize.width < 768 && CD_STATE.collectionDisrupter?.collectionDescriptionMobile !== '') {
    textDescription = CD_STATE.collectionDisrupter?.collectionDescriptionMobile || ''
  } else {
    textDescription = CD_STATE.collectionDisrupter?.collectionDescription || ''
  }

  const moreIndex = textDescription.indexOf('[more]')
  const hasMore = moreIndex !== -1
  const textBeforeMore = hasMore ? (textDescription.slice(0, moreIndex) + (showEllipsis ? '...' : '')) : textDescription
  const textAfterMore = hasMore ? textDescription.slice(moreIndex + 6) : ""

  const memoizedTriggerSearch = useCallback(() => {
    triggerSearch()
  }, [triggerSearch])

    // Draggable functionality
    useEffect(() => {
      const scrollableDiv = scrollableDivRef.current
      if (!scrollableDiv) return
  
      let isDragging = false
      let startX = 0
      let scrollLeft = 0
  
      const handleMouseDown = (e: MouseEvent) => {
        isDragging = true
        startX = e.pageX - scrollableDiv.offsetLeft
        scrollLeft = scrollableDiv.scrollLeft
        scrollableDiv.style.cursor = 'grabbing'
      };
  
      const handleMouseLeave = () => {
        isDragging = false
        scrollableDiv.style.cursor = 'grab'
      };
  
      const handleMouseUp = () => {
        isDragging = false
        scrollableDiv.style.cursor = 'grab'
      };
  
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return
        e.preventDefault()
        const x = e.pageX - scrollableDiv.offsetLeft
        const walk = (x - startX) * 2
        scrollableDiv.scrollLeft = scrollLeft - walk
      }
  
      scrollableDiv.addEventListener('mousedown', handleMouseDown)
      scrollableDiv.addEventListener('mouseleave', handleMouseLeave)
      scrollableDiv.addEventListener('mouseup', handleMouseUp)
      scrollableDiv.addEventListener('mousemove', handleMouseMove)
  
      return () => {
        scrollableDiv.removeEventListener('mousedown', handleMouseDown)
        scrollableDiv.removeEventListener('mouseleave', handleMouseLeave)
        scrollableDiv.removeEventListener('mouseup', handleMouseUp)
        scrollableDiv.removeEventListener('mousemove', handleMouseMove)
      }
    }, [])

  // The collections have pre populated quick link call outs from metafields
  const subCollectionHandler = (): JSX.Element => {
    const quickLinksToRender: JSX.Element[] = []


    if (hasLocalCollectionSubFilters.length) {
      hasLocalCollectionSubFilters?.map((collection: SubCollectionLinks) => {
        quickLinksToRender.push(<SubCollectionFilter collectionName={collection.label} collectionUrl={collection.url} fromMetaObject={true} />)
      })
    } else {
      hasGlobalCollectionSubfilters?.map((collection: SubCollectionLinks) => {
        quickLinksToRender.push(<SubCollectionFilter collectionName={collection.label} collectionUrl={collection.url} />)
      })
    }

    return (
      <>
      <div ref={scrollableDivRef} className={`flex justify-between flex-shrink-0 scrollable ${isTablet ? '' : 'overflow-x-auto relative max-w-[98%]'}`}>
        <div className="flex items-center">
          <Filter filters={filters} sorting={sorting} triggerSearch={memoizedTriggerSearch} activeFilters={activeFilters} />
          {quickLinksToRender.map((component, index) => (
            <div key={index}>{component}</div>
          ))}
        </div>
      </div>
      </>
    )
  }

  useEffect(() => {
    if (windowSize.width < 768) {
      setShowEllipsis(true)
    } else setShowEllipsis(false)
  }, [])

  return (
    <>
      <div className="px-5">
        <div className="min-h-[169px] lg:min-h-[150px] pt-DXL pb-DMD lg:py-DLG lg:flex lg:gap-5">
          <h1 data-nosnippet className="uppercase font-bstSpyre text-lg-heading lg:text-mdlg-heading lg:mb-0 lg:w-1/2 lg-max:mb-2.5">{`${CD_STATE.searchspring.collectionName ? CD_STATE.searchspring.collectionName : ''}`}</h1>
          <div className='lg:w-1/2'>
            {textBeforeMore && <div className='font-bstSpyre text-sm-c font-light text-ink [&_a]:underline [&_p]:mb-2 [&_p:last-child]:mb-0' dangerouslySetInnerHTML={{ __html: displayMore ? textBeforeMore + textAfterMore : textBeforeMore }}></div>}
            {hasMore && (
              <button className='mt-2.5 underline text-sm-c text-ink font-light font-bstSpyre' onClick={() => setDisplayMore(!displayMore)}>
                {displayMore ? CD_STATE.collectionDisrupter?.readLess : CD_STATE.collectionDisrupter?.readMore}
              </button>
            )}
          </div>
        </div>
        {hasGlobalCollectionSubfilters || hasLocalCollectionSubFilters ?
          <>
            <div className="pr-MD pt-SM pb-MD">
              <div className={`flex items-center justify-between ${isTablet ? 'max-w-full overflow-x-auto custom-scrollbar' : 'max-w-[90%] overflow-x-auto custom-scrollbar'}`}>
                {subCollectionHandler()}
                {hasReadyToShipFilter && !isTablet && <ReadyToShipFilter />}
              </div>
            </div>
            {hasReadyToShipFilter && isTablet && <div className='pb-LG'><ReadyToShipFilter isTablet={isTablet} /></div>}
          </> 
        :
          <div className="flex justify-between px-MD pt-SM pb-MD items-center">
            <Filter filters={filters} sorting={sorting} triggerSearch={memoizedTriggerSearch} activeFilters={activeFilters} />
            {hasReadyToShipFilter && <ReadyToShipFilter />}
          </div>
        }
      </div>
    </>
  )
}

export default CollectionPageHeader